import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { RefreshCw } from 'lucide-react';
import { useAuth } from '../AuthProvider';
import AdminNav from './AdminNav';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const OrphanedEntriesPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [gcsData, setGcsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOrphanedEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!token) {
        setError('Authentication token not available. Please log in again.');
        setLoading(false);
        return;
      }
      const headers = { Authorization: `Bearer ${token}` };
      const [entriesRes, gcsRes] = await Promise.all([
        axios.get(`${API_BASE}/admin/orphaned-entries`, { headers }),
        axios.get(`${API_BASE}/admin/orphaned-gcs-assets`, { headers }),
      ]);
      setData(entriesRes.data);
      setGcsData(gcsRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load orphaned entries.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchOrphanedEntries();
  }, [fetchOrphanedEntries]);

  const renderTable = (title, items, columns, emptyMessage) => {
    if (!items || items.length === 0) {
      return (
        <div className="bg-card rounded-lg border border-border p-6">
          <h3 className="text-lg font-semibold text-foreground mb-3">{title}</h3>
          <p className="text-muted-foreground text-sm">{emptyMessage}</p>
        </div>
      );
    }
    return (
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <h3 className="text-lg font-semibold text-foreground px-6 py-3 border-b border-border bg-muted/30">
          {title} ({items.length})
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/20">
                {columns.map((col) => (
                  <th key={col.key} className="text-left px-4 py-2 font-medium text-muted-foreground">
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((row, idx) => (
                <tr key={row.id || idx} className="border-b border-border hover:bg-muted/10">
                  {columns.map((col) => (
                    <td key={col.key} className="px-4 py-2 text-foreground">
                      {col.render ? col.render(row[col.key], row) : String(row[col.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full w-full overflow-y-auto bg-gray-100 dark:bg-neutral-950 p-8">
      <div className="max-w-7xl mx-auto pb-12">
        <div className="flex justify-between items-center mb-8">
          <div className="flex flex-col">
            <h1 className="text-3xl font-medium text-gray-900 dark:text-neutral-100">Admin Panel</h1>
            <p className="text-sm text-gray-500 dark:text-neutral-400 mt-2">
              Orphaned DB entries and empty projects (read-only). Use to find dev trash.
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={fetchOrphanedEntries}
              disabled={loading}
              className="text-gray-500 hover:text-gray-700 dark:text-neutral-400 dark:hover:text-neutral-200 text-sm font-medium whitespace-nowrap border border-gray-300 dark:border-neutral-700 rounded-md px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors flex items-center disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => navigate('/')}
              className="text-gray-500 hover:text-gray-700 dark:text-neutral-400 dark:hover:text-neutral-200 text-sm font-medium whitespace-nowrap border border-gray-300 dark:border-neutral-700 rounded-md px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors"
            >
              ← Back to App
            </button>
          </div>
        </div>

        <AdminNav />

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="bg-card rounded-lg border border-border p-8 text-center text-muted-foreground">
            Loading orphaned entries...
          </div>
        ) : data ? (
          <div className="space-y-6">
            {data?.summary && (
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                <div className="bg-card rounded-lg border border-border p-4">
                  <p className="text-xs font-medium text-muted-foreground uppercase">Orphaned project_files</p>
                  <p className="text-2xl font-semibold text-foreground">{data.summary.orphaned_project_files_count}</p>
                </div>
                <div className="bg-card rounded-lg border border-border p-4">
                  <p className="text-xs font-medium text-muted-foreground uppercase">Orphaned transcription</p>
                  <p className="text-2xl font-semibold text-foreground">{data.summary.orphaned_generated_transcription_count}</p>
                </div>
                <div className="bg-card rounded-lg border border-border p-4">
                  <p className="text-xs font-medium text-muted-foreground uppercase">Orphaned keywords</p>
                  <p className="text-2xl font-semibold text-foreground">{data.summary.orphaned_generated_keywords_count}</p>
                </div>
                <div className="bg-card rounded-lg border border-border p-4">
                  <p className="text-xs font-medium text-muted-foreground uppercase">Empty projects</p>
                  <p className="text-2xl font-semibold text-foreground">{data.summary.empty_projects_count}</p>
                </div>
                <div className="bg-card rounded-lg border border-border p-4">
                  <p className="text-xs font-medium text-muted-foreground uppercase">Orphaned GCS assets</p>
                  <p className="text-2xl font-semibold text-foreground">{gcsData?.summary?.orphaned_gcs_assets_count ?? '—'}</p>
                </div>
              </div>
            )}

            {renderTable(
              'Orphaned project_files (project no longer exists)',
              data.orphaned_project_files,
              [
                { key: 'id', label: 'ID' },
                { key: 'project_id', label: 'project_id' },
                { key: 'gcs_path', label: 'gcs_path' },
                { key: 'created_at', label: 'created_at', render: (v) => (v ? new Date(v).toLocaleString() : '—') },
              ],
              'No orphaned project_files.'
            )}

            {renderTable(
              'Orphaned generated_transcription',
              data.orphaned_generated_transcription,
              [
                { key: 'id', label: 'ID' },
                { key: 'project_id', label: 'project_id' },
                { key: 'project_file_id', label: 'project_file_id' },
                { key: 'transcript_source', label: 'transcript_source' },
                { key: 'created_at', label: 'created_at', render: (v) => (v ? new Date(v).toLocaleString() : '—') },
              ],
              'No orphaned generated_transcription.'
            )}

            {renderTable(
              'Orphaned generated_keywords',
              data.orphaned_generated_keywords,
              [
                { key: 'id', label: 'ID' },
                { key: 'project_file_id', label: 'project_file_id' },
                { key: 'transcript_source', label: 'transcript_source' },
                { key: 'created_at', label: 'created_at', render: (v) => (v ? new Date(v).toLocaleString() : '—') },
              ],
              'No orphaned generated_keywords.'
            )}

            {renderTable(
              'Empty projects (no project_files, no generated_transcription)',
              data.empty_projects,
              [
                { key: 'id', label: 'ID' },
                { key: 'name', label: 'name' },
                { key: 'project_type', label: 'project_type' },
                { key: 'status', label: 'status' },
                { key: 'created_at', label: 'created_at', render: (v) => (v ? new Date(v).toLocaleString() : '—') },
              ],
              'No empty projects.'
            )}

            {gcsData?.error && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <p className="text-amber-800 dark:text-amber-200 text-sm">GCS assets: {gcsData.error}</p>
              </div>
            )}
            {renderTable(
              'Orphaned GCS assets (in media buckets, not referenced by project_files)',
              gcsData?.orphaned_gcs_assets,
              [
                { key: 'bucket', label: 'bucket' },
                { key: 'path', label: 'path' },
                { key: 'gs_uri', label: 'gs_uri' },
              ],
              'No orphaned GCS assets.'
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default OrphanedEntriesPage;
